import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts'

export default function AnalysisPage({ profile, athleteId, onBack }) {
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('rpe')
  const [logs, setLogs] = useState([])
  const [hillLogs, setHillLogs] = useState([])

  useEffect(() => {
    if (athleteId) fetchData()
  }, [athleteId])

  const fetchData = async () => {
    setLoading(true)

    // Ambil session logs + session info
    const { data: sessionLogs } = await supabase
      .from('session_logs')
      .select(`
        *,
        sessions (session_date, day_type, mode)
      `)
      .eq('athlete_id', athleteId)
      .order('created_at', { ascending: true })
      .limit(30)

    // Ambil hill repeat logs
    const { data: hillData } = await supabase
      .from('hill_repeat_logs')
      .select(`
        *,
        session_logs!inner (
          athlete_id,
          sessions (session_date)
        )
      `)
      .eq('session_logs.athlete_id', athleteId)
      .order('created_at', { ascending: true })

    setLogs(sessionLogs || [])
    setHillLogs(hillData || [])
    setLoading(false)
  }

  // Format data untuk chart RPE vs Speed
  const rpeSpeedData = logs
    .filter(l => l.rpe && l.avg_speed)
    .map(l => ({
      date: l.sessions?.session_date?.slice(5) || '-',
      RPE: l.rpe,
      Speed: parseFloat(l.avg_speed),
      HR: l.heart_rate || null,
      type: l.sessions?.day_type || '-'
    }))

  // Format data cadence trend
  const cadenceData = logs
    .filter(l => l.cadence)
    .map(l => ({
      date: l.sessions?.session_date?.slice(5) || '-',
      Cadence: l.cadence,
      type: l.sessions?.day_type || '-'
    }))

  // Avg speed per mode road passing
  const roadModes = ['Interval', 'Tim Rantai', 'ITT']
  const roadData = roadModes.map(mode => {
    const filtered = logs.filter(l =>
      l.sessions?.day_type === 'road_passing' &&
      l.sessions?.mode === mode &&
      l.avg_speed
    )
    return {
      mode,
      avgSpeed: filtered.length
        ? (filtered.reduce((s, l) => s + parseFloat(l.avg_speed), 0) / filtered.length).toFixed(1)
        : 0,
      count: filtered.length
    }
  })

  // Long ride flat vs hilly
  const longRideData = logs
    .filter(l => l.sessions?.day_type === 'long_ride' && l.avg_speed)
    .map(l => ({
      date: l.sessions?.session_date?.slice(5) || '-',
      Speed: parseFloat(l.avg_speed),
      Distance: parseFloat(l.distance || 0),
      HR: l.heart_rate || null,
    }))

  // Hill repeat — avg speed per rep per sesi
  const hillBySession = hillLogs.reduce((acc, h) => {
    const date = h.session_logs?.sessions?.session_date?.slice(5) || '-'
    const key = `${date}-S${h.session_number}`
    if (!acc[key]) acc[key] = { key, reps: [] }
    acc[key].reps.push(parseFloat(h.avg_speed || 0))
    return acc
  }, {})

  const hillChartData = Object.values(hillBySession).map(s => ({
    sesi: s.key,
    avgSpeed: s.reps.length
      ? (s.reps.reduce((a, b) => a + b, 0) / s.reps.length).toFixed(1)
      : 0
  }))

  const TABS = [
    { id: 'rpe', label: '💪 RPE vs Speed' },
    { id: 'cadence', label: '⚙️ Cadence' },
    { id: 'road', label: '⚡ Road Passing' },
    { id: 'hill', label: '🏔️ Hill Repeat' },
    { id: 'longride', label: '🗺️ Long Ride' },
  ]

  const tooltipStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '0.75rem'
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e'
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: '1px solid #334155',
          color: '#94a3b8', padding: '0.4rem 0.75rem',
          borderRadius: '8px', cursor: 'pointer',
          fontSize: '0.85rem', marginRight: '1rem'
        }}>
          ← Kembali
        </button>
        <div>
          <div style={{ fontWeight: 'bold', color: '#34d399' }}>📊 Analisis Performa</div>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{profile?.full_name}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', overflowX: 'auto',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e', padding: '0 1rem'
      }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '0.75rem 1rem',
            background: 'none', border: 'none',
            borderBottom: activeTab === tab.id ? '2px solid #34d399' : '2px solid transparent',
            color: activeTab === tab.id ? '#34d399' : '#94a3b8',
            cursor: 'pointer', fontSize: '0.78rem',
            fontWeight: activeTab === tab.id ? 'bold' : 'normal',
            whiteSpace: 'nowrap', marginBottom: '-1px'
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '1rem 1.5rem' }}>

        {loading && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
            Loading data...
          </div>
        )}

        {!loading && logs.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '3rem',
            color: '#94a3b8'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Belum ada data sesi</div>
            <div style={{ fontSize: '0.8rem' }}>Input sesi latihan dulu untuk melihat analisis</div>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <>
            {/* RPE vs Speed */}
            {activeTab === 'rpe' && (
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 'bold', marginBottom: '1rem' }}>
                  RPE vs Avg Speed vs Heart Rate — 30 sesi terakhir
                </div>
                {rpeSpeedData.length === 0 ? (
                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                    Belum ada data RPE + Speed
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={rpeSpeedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                      <YAxis yAxisId="left" stroke="#fb923c" fontSize={11} domain={[0, 10]} />
                      <YAxis yAxisId="right" orientation="right" stroke="#38bdf8" fontSize={11} />
                      <YAxis yAxisId="hr" orientation="right" stroke="#ef4444" fontSize={11} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                      <Line yAxisId="left" type="monotone" dataKey="RPE"
                        stroke="#fb923c" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="right" type="monotone" dataKey="Speed"
                        stroke="#38bdf8" strokeWidth={2} dot={{ r: 3 }} />
                     <Line yAxisId="hr" type="monotone" dataKey="HR"
  stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }}
  connectNulls={true} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {/* Stats summary */}
                {rpeSpeedData.length > 0 && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '0.75rem', marginTop: '1rem'
                  }}>
                    {[
                      {
                        label: 'Avg RPE',
                        value: (rpeSpeedData.reduce((s, d) => s + d.RPE, 0) / rpeSpeedData.length).toFixed(1),
                        color: '#fb923c'
                      },
                      {
                        label: 'Avg Speed',
                        value: (rpeSpeedData.reduce((s, d) => s + d.Speed, 0) / rpeSpeedData.length).toFixed(1) + ' km/j',
                        color: '#38bdf8'
                      },
                      {
                         label: 'Avg HR',
                          value: (() => {
                            const hrData = rpeSpeedData.filter(d => d.HR)
                            return hrData.length > 0
                                ? Math.round(hrData.reduce((s, d) => s + d.HR, 0) / hrData.length) + ' bpm'
                                : '-'
                        })(),
                        color: '#ef4444'
                    },
                      {
                        label: 'Total Sesi',
                        value: logs.length,
                        color: '#34d399'
                      },
                    ].map(s => (
                      <div key={s.label} style={{
                        background: `${s.color}18`,
                        border: `1px solid ${s.color}44`,
                        borderRadius: '10px', padding: '0.75rem',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: s.color }}>
                          {s.value}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cadence trend */}
            {activeTab === 'cadence' && (
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 'bold', marginBottom: '1rem' }}>
                  Trend Cadence — 30 sesi terakhir
                </div>
                {cadenceData.length === 0 ? (
                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                    Belum ada data cadence
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={cadenceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                      <YAxis stroke="#f472b6" fontSize={11} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Line type="monotone" dataKey="Cadence"
                        stroke="#f472b6" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {cadenceData.length > 0 && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '0.75rem', marginTop: '1rem'
                  }}>
                    {[
                      {
                        label: 'Avg Cadence',
                        value: Math.round(cadenceData.reduce((s, d) => s + d.Cadence, 0) / cadenceData.length) + ' rpm',
                        color: '#f472b6'
                      },
                      {
                        label: 'Max Cadence',
                        value: Math.max(...cadenceData.map(d => d.Cadence)) + ' rpm',
                        color: '#facc15'
                      },
                      {
                        label: 'Min Cadence',
                        value: Math.min(...cadenceData.map(d => d.Cadence)) + ' rpm',
                        color: '#94a3b8'
                      },
                    ].map(s => (
                      <div key={s.label} style={{
                        background: `${s.color}18`,
                        border: `1px solid ${s.color}44`,
                        borderRadius: '10px', padding: '0.75rem',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: s.color }}>
                          {s.value}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Road passing per mode */}
            {activeTab === 'road' && (
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 'bold', marginBottom: '1rem' }}>
                  Avg Speed per Mode Road Passing
                </div>
                {roadData.every(d => d.count === 0) ? (
                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                    Belum ada data road passing
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={roadData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="mode" stroke="#475569" fontSize={11} />
                        <YAxis stroke="#facc15" fontSize={11} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="avgSpeed" fill="#facc15" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{
                      display: 'flex', gap: '0.75rem',
                      marginTop: '1rem', flexWrap: 'wrap'
                    }}>
                      {roadData.map(d => (
                        <div key={d.mode} style={{
                          flex: 1, minWidth: '80px',
                          background: '#facc1518',
                          border: '1px solid #facc1544',
                          borderRadius: '10px', padding: '0.75rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#facc15' }}>
                            {d.avgSpeed} <span style={{ fontSize: '0.7rem' }}>km/j</span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{d.mode}</div>
                          <div style={{ fontSize: '0.65rem', color: '#475569' }}>{d.count} sesi</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Hill repeat */}
            {activeTab === 'hill' && (
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 'bold', marginBottom: '1rem' }}>
                  Progress Hill Repeat — Avg Speed per Sesi
                </div>
                {hillChartData.length === 0 ? (
                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                    Belum ada data hill repeat
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={hillChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="sesi" stroke="#475569" fontSize={10} />
                      <YAxis stroke="#f87171" fontSize={11} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="avgSpeed" fill="#f87171" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Long ride */}
            {activeTab === 'longride' && (
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 'bold', marginBottom: '1rem' }}>
                  Trend Long Ride — Speed & Jarak
                </div>
                {longRideData.length === 0 ? (
                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                    Belum ada data long ride
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={longRideData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                      <YAxis yAxisId="left" stroke="#60a5fa" fontSize={11} />
                      <YAxis yAxisId="right" orientation="right" stroke="#4ade80" fontSize={11} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                      <Line yAxisId="left" type="monotone" dataKey="Speed"
                        stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="right" type="monotone" dataKey="Distance"
                        stroke="#4ade80" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

